import { Col, Flex, Row } from "antd"
import React, { useEffect, useRef, useState, useLayoutEffect } from "react"
import NumberScroller from "@/components/NumberScroller"
import Charts from "../site/inverter/components/Charts"
import { CaretUpOutlined, CaretDownOutlined } from "@ant-design/icons"

const CurrentBg: React.FC = () => {
  return (
    <div className="waterbox">
      <img className="cbg" src="/assets/image/cbg.png" />
      <div className="wave" id="wave1"></div>
      <div className="wave" id="wave2"></div>
    </div>
  )
}

const debounce = (func: any, wait: any) => {
  let timeout: any
  return (...args: any) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(this, args), wait)
  }
}

const ComponentName: React.FC = (props) => {
  const [chartsHeight, setChartsHeight] = useState<number>(0)
  const overviewRightRef = useRef<HTMLDivElement | null>(null)

  const updateHeight = () => {
    if (overviewRightRef.current) {
      const newHeight = overviewRightRef.current.clientHeight - 80
      if (newHeight !== chartsHeight) {
        setChartsHeight(newHeight) // Only update if the height changes
      }
    }
  }

  useEffect(() => {
    // Initial update
    updateHeight()

    // Listen to window resize
    window.addEventListener("resize", debounce(updateHeight, 300))

    // Cleanup on component unmount
    return () => {
      window.removeEventListener("resize", updateHeight)
    }
  }, []) // Run only on mount

  useLayoutEffect(() => {
    updateHeight() // Initial layout height adjustment
  }, [overviewRightRef.current]) // This dependency can also be refined

  // Initial data setup
  const datas = [
    {
      label: "Total Production Today",
      icon: "day.svg",
      value: 1.21,
      unit: "kWh",
      compare: 0.03,
    },
    {
      label: "Total Production Yesterday",
      icon: "yeserday.svg",
      value: 1125.21,
      unit: "kWh",
      compare: 0.01,
    },
    {
      label: "Total Production This Month",
      icon: "mouth.svg",
      value: 1125.21,
      unit: "kWh",
      compare: -0.03,
    },
    {
      label: "Total Production This Year",
      icon: "yeserday.svg",
      value: 1125.21,
      unit: "kWh",
      compare: -0.03,
    },
  ]

  const SystemData = [
    { label: "Total plant", value: 10, icon: "total.svg" },
    { label: "System capacity", value: 10, icon: "capacity.svg", unit: "kWp" },
    {
      label: "Battery capacity",
      value: 10,
      icon: "batteryCapacity.svg",
      unit: "kWp",
    },
  ]

  const DeviceData = [
    { label: "Online devices", value: 10, icon: "donline.svg" },
    { label: "Offline devices", value: 10, icon: "doffine.svg", unit: "kWp" },
    {
      label: "Faulty devices",
      value: 10,
      icon: "dfaulty.svg",
      unit: "kWp",
    },
  ]

  const GreenAndCarbon: any = [
    { label: "CO2 emission reduction (t)", value: 123.0, icon: "co2.svg" },
    { label: "Planted", value: 123.0, icon: "tree.svg" },
    { label: "Coal savings", value: 123.0, icon: "yc.svg" },
  ]

  const RevenueStatistics = [
    {
      label: "Revenue today",
      value: 11234,
      icon: "jb.svg",
      compare: 0.005,
      compareText: "last daily",
    },
    {
      label: "Revenue this month",
      value: 11234,
      icon: "wb.svg",
      compare: 0.005,
      compareText: "last month",
    },
    {
      label: "Revenue total",
      value: 11234,
      icon: "qb.svg",
      compare: -0.005,
      compareText: "last daily",
    },
  ]

  return (
    <div id="OverView">
      <Flex className="overview-one" gap={15}>
        <div className="overview-one1 overview-card">
          <Flex vertical gap={25} className="overview-one1-title">
            <span>Current Power</span>
            <Flex gap={10} align="baseline">
              <NumberScroller targetNumber={150.51} />
              <span className="unit">kW</span>
            </Flex>
          </Flex>
          <CurrentBg />
        </div>
        {datas.map((v: any, index: number) => (
          <div className="overview-one2 overview-card" key={index}>
            <div className="overview-one2-title">
              <Flex gap={5} align="center">
                {v.label}
                {v.compare !== 0 && (
                  <span className={`overview-one2-title-compare compare${v.compare > 0 ? "up" : "down"}`}>
                    <Flex gap={2}>
                      {v.compare > 0 && <img src="/assets/web/up.svg" />}
                      {v.compare < 0 && <img src="/assets/web/down.svg" />}
                      {Math.abs(v.compare * 100).toFixed(2)}%
                    </Flex>
                  </span>
                )}
              </Flex>
            </div>
            <div className="overview-one2-center">
              <Flex vertical gap={15} justify="center" style={{ minWidth: "50%" }} align="center">
                <div className="overview-one2-center-icon">
                  <img src={`/assets/web/${v.icon}`} />
                </div>
                <Flex align="baseline" gap={5}>
                  <NumberScroller targetNumber={v.value} />
                  <span>{v.unit}</span>
                </Flex>
              </Flex>
            </div>
          </div>
        ))}
      </Flex>

      <Flex className="overview-two" gap={15}>
        <div className="overview-two-left">
          <Charts style={{ width: "100%", height: `${chartsHeight}px` }} />
        </div>
        <div className="overview-two-right" ref={overviewRightRef}>
          <div className="overview-two-right-one sbg">
            <div className="overview-two-right-title">System Data</div>
            <Flex justify="space-between">
              {SystemData.map((v) => (
                <Flex className="overview-item" vertical gap={4} align="center" key={v.label}>
                  <div className="overview-item-icon">
                    <img src={`/assets/web/${v.icon}`} />
                  </div>
                  <Flex className="overview-item-value" align="baseline" gap={3}>
                    <NumberScroller targetNumber={v.value} />
                    <span className="unit" style={{ color: "#999" }}>
                      {v.unit}
                    </span>
                  </Flex>
                  <div className="overview-item-label">{v.label}</div>
                </Flex>
              ))}
            </Flex>
            <div className="space-line"></div>
            <div className="overview-two-right-title">Device Data</div>
            <Flex justify="space-between">
              {DeviceData.map((v) => (
                <Flex className="overview-item" vertical gap={4} align="center" key={v.label}>
                  <div className="overview-item-icon">
                    <img src={`/assets/web/${v.icon}`} />
                  </div>
                  <Flex className="overview-item-value" align="baseline" gap={3}>
                    <NumberScroller targetNumber={v.value} />
                    <span className="unit" style={{ color: "#999" }}>
                      {v.unit}
                    </span>
                  </Flex>
                  <div className="overview-item-label">{v.label}</div>
                </Flex>
              ))}
            </Flex>
          </div>

          <div className="overview-two-right-one gbg">
            <div className="overview-two-right-title">Green & Carbon Reduction Data</div>
            <Flex justify="space-between">
              {GreenAndCarbon.map((v: any) => (
                <Flex className="overview-item" vertical gap={4} align="center" key={v.label}>
                  <div className="overview-item-icon">
                    <img src={`/assets/web/${v.icon}`} />
                  </div>
                  <Flex className="overview-item-value" align="baseline" gap={3}>
                    <NumberScroller targetNumber={v.value} />
                    <span className="unit" style={{ color: "#999" }}>
                      {v.unit}
                    </span>
                  </Flex>
                  <div className="overview-item-label">{v.label}</div>
                </Flex>
              ))}
            </Flex>
          </div>

          <div className="overview-two-right-one RevenueStatistics-card mbg">
            <div className="overview-two-right-title">Revenue Statistics</div>
            <Flex justify="space-between">
              {RevenueStatistics.map((v) => (
                <Flex gap={8} justify="space-between" vertical className="RevenueStatistics" key={v.label}>
                  <Flex gap={8} align="center">
                    <img src={`/assets/web/${v.icon}`} />
                    <Flex align="baseline" gap={1} className="money-text">
                      <span className="unit">$</span>
                      <NumberScroller type="money" targetNumber={v.value} />
                    </Flex>
                  </Flex>
                  <Flex className="money-value" gap={5}>
                    <span>{v.compareText}</span>
                    <Flex gap={1}>
                      <span
                        style={{
                          fontWeight: "700",
                          color: v.compare > 0 ? "var(--main-success)" : "var(--main-error)",
                        }}>
                        {Math.abs(v.compare * 100).toFixed(2)}%
                      </span>
                      {v.compare > 0 && <CaretUpOutlined style={{ color: "var(--main-success)" }} />}
                      {v.compare < 0 && <CaretDownOutlined style={{ color: "var(--main-error)" }} />}
                    </Flex>
                  </Flex>
                  <div className="money-label">{v.label}</div>
                </Flex>
              ))}
            </Flex>
          </div>
        </div>
      </Flex>
    </div>
  )
}

export default ComponentName
